<template>
  <div id="map" ref="map">
    <div id="basemap" class="mapboxgl-ctrl-top-right mapboxgl-ctrl">
      <div
          class="mapboxgl-ctrl mapboxgl-ctrl-group basemaps">
        <BaseMapSelect v-on:style-change="setMapStyle($event)"/>
      </div>
    </div>
    <div ref="mapgl" class="mapboxgl-map"></div>
    <MapboxLayer
        name="buildings"
        v-bind:before-layer-name=beforeLayers
        v-bind:url="'mapbox://semcog.a8onykt7'"
        v-bind:layers="buildings_style"
        v-bind:interactivity=true
        v-on:featureClick="buildings_update_popup($event)"
    />
    <AmenitySelect
        class="map-overlay no-print"
        v-bind:mappedAmenity="mappedAmenity"
        v-bind:amenities="amenities"
        v-bind:amenityNames="amenityNames"
        v-bind:counties="counties"
        v-bind:mcd="mcdFilter"
        v-bind:startExpanded="startExpanded"
        v-on:input="(mappedAmenity = $event) && (startStopAnimation())"
        v-on:mcdInput="mcdFilter = $event"
        v-on:countiesInput="$emit('set-counties', $event)"
        v-on:expandedInput="amenitySelectExpanded = $event"
    />
    <div  class="map-overlay" id="legend">
      <div v-if="mappedAmenity === 'year_built'" class="map-overlay-inner">
        <div>Current Year: <strong>{{yearCutOff}}</strong></div>
        <label id="yearbuilt"></label>
        <button style="vertical-align: text-bottom;"><font-awesome-icon v-on:click="startStopAnimation" v-bind:icon="animationCaller ? 'pause' : 'play'"/></button>
        <input id="slider" type="range" min="1900" max="2020" step="10" v-model.number="yearCutOff"/>
      </div>
            <table>
                <tr>
                    <th
                    >{{amenityNames[mappedAmenity].name}}</th>
                </tr>
                <tr
                        v-for="leg in amenityNames[mappedAmenity].colors"
                        v-bind:key="leg.label"
                >
                  <td
                    v-bind:style="{
                        background: leg.value,
                      }"
                    v-bind:class="{
                        activate: mappedAmenity !== 'year_built' || leg.maxYear <= yearCutOff, text_halo: mappedAmenity === 'year_built'
                      }"
                    v-on:click="mappedAmenity !== 'year_built' || (yearCutOff = leg.maxYear)"
                  >{{ leg.label }}
                  </td>
                </tr>
            </table>
        </div>
    <div class="loader" v-if="loaded">
      <div class="CDB-LoaderIcon CDB-LoaderIcon--big">
        <svg class="CDB-LoaderIcon-spinner" viewBox="0 0 50 50">
          <circle class="CDB-LoaderIcon-path" cx="25" cy="25" r="20" fill="none"/>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import MapboxLayer from "./MapboxLayer";
import BaseMapSelect from "./BaseMapSelect";
import AmenitySelect from "./AmenitySelect";
import * as turf from 'turf'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

export default {
  name: "mainmap",
  components: {MapboxLayer, BaseMapSelect, AmenitySelect},
  props: [],
  data: function () {
    let qcounty = '';

    let county = [];
    if (qcounty) {
      if (Array.isArray(qcounty)) {
        county = qcounty.map(m => {
          return {statevalue: parseInt(m, 10)}
        });
      } else {
        county = [{statevalue: parseInt(qcounty, 10)}]
      }
    }
    return {
      mappedAmenity: 'building_type',
      yearCutOff: 2060,
      animationCaller: undefined,
      amenityNames: {
        "building_type": {
          "name": "Building Type",
          "colors": [{
        key: "institutional",
        value: "#2e5569",
        visibility: true,
        label: "Institutional"
      },
        {
          key: "office & retail",
          value: "#d22222",
          visibility: true,
          label: "Office & Retail"
        },
        {
          key: "industrial",
          value: "#cc99cc",
          visibility: true,
          label: "Industrial"
        },
        {
          key: "tcu",
          value: "#cdcdcd",
          visibility: true,
          label: "Transportation & Utility"
        },
        {
          key: "medical",
          value: "#f57ab6",
          visibility: true,
          label: "Medical"
        },
        {
          key: "leisure & hospitality",
          value: "#629d46",
          visibility: true,
          label: "Leisure & Hospitality"
        },
        {
          key: "agricultural",
          value: "#ffff00",
          visibility: true,
          label: "Agricultural"
        },
        {
          key: "single-family residential",
          value: "#ffd37f",
          visibility: true,
          label: "Single-family Residential"
        },
        {
          key: "multi-family residential",
          value: "#e69800",
          visibility: true,
          label: "Multi-family Residential"
        },
        {
          key: "manufactured housing park",
          value: "#993333",
          visibility: true,
          label: "Manufactured Housing Park"
        }]
        },
        "year_built": {
          "name": 'Year Built',
          "colors": [{ //#f3e79b,#fac484,#f8a07e,#eb7f86,#ce6693,#a059a0,#5c53a5
        minYear: 0,
        maxYear: 0,
        value: "#414141",
        label: "N/A"
      },{ //#f3e79b,#fac484,#f8a07e,#eb7f86,#ce6693,#a059a0,#5c53a5
        minYear: 1500,
        maxYear: 1899,
        value: "#880b0b",
        label: "<1900"
      }, {
        minYear: 1900,
        maxYear: 1924,
        value: "#ef2e2e",
        label: "1900 - 1924"
      }, {
        minYear: 1925,
        maxYear: 1949,
        value: "#e9952e",
        label: "1925 - 1949"
      }, {
        minYear: 1950,
        maxYear: 1974,
        value: "#289dcb",
        label: "1950 - 1974"
      }, {
        minYear: 1975,
        maxYear: 1999,
        value: "#419b5b",
        label: "1975 - 1999"
      }, {
        minYear: 2000,
        maxYear: 2020,
        value: "#124e0c",
        label: "2000 - 2020"
      }]
        }
      },
      amenities: ["building_type", "year_built"],
      counties: county,
      regionBoundarys: undefined,
      mcdFilter: 0,
      startExpanded: false,
      amenitySelectExpanded: this.startExpanded,
      zoomControl: new mapboxgl.NavigationControl({visualizePitch: true}),
      loaded: false,
      currentPopup: new mapboxgl.Popup({maxWidth: 'none'}),
    }
  },
  computed: {
    map: function () {
      mapboxgl.accessToken = 'pk.eyJ1Ijoic2VtY29nIiwiYSI6ImlvNEFUcmMifQ.VmDLZZG7l6syscFzkBs5eg'
      return new mapboxgl.Map({
        container: this.$refs.mapgl,
        style: 'mapbox://styles/semcog/ckdis0em00v8t1jmfju52ryu3',
        center: [-83.04886, 42.33185],
        pitch: 59,
        bearing:-40.1,
        zoom: 15.01,
        hash: true,
        minZoom: 10.5,
        maxBounds: [-87, 40, -79, 44],
        scrollZoom: true,
      }).addControl(new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        bbox: [-85.11, 41.55, -81.46, 43.32],
        collapsed: true,
        marker: true,
        flyTo: true,
        mapboxgl: mapboxgl,
      }), 'bottom-left').addControl(this.zoomControl, 'bottom-left');
    },
    beforeLayers: function () {
      return ['mi-county-labelpoints', 'waterway-label', 'place-village', 'mcd-pop-label-2uiaf9', 'local-dx1f7q']
    },
    boundary: function () {
      if (!this.regionBoundarys) {
        return this.regionBoundarys
      }
      if (Array.isArray(this.mcdFilter)) {
        return turf.union(turf.featureCollection(this.mcdFilter.map(m =>
            turf.bboxPolygon(turf.bbox(this.regionBoundarys[m] || this.regionBoundarys[0])))))
      }
      return turf.rewind(this.regionBoundarys[this.mcdFilter || 0])
    },
    buildings_year: function () {
      let filter = ['in', ['get', 'city_id'], ["literal", this.mcdFilter]]
      if (!this.mcdFilter || this.mcdFilter.length === 0) {
        filter = true
      } else if (this.mcdFilter && !this.mcdFilter.length){
        filter = ['==', ['get', "city_id"], this.mcdFilter]
      }

      filter = ["all", filter, ['<=', ['get', 'year_built'], this.yearCutOff]]

      let color = [
        'step',
        ['get', 'year_built']]
      color.push('#ccc')
      this.amenityNames[this.mappedAmenity].colors.forEach(c => {
        color.push(c.minYear, c.value)
      })
      return [
        {
          type: 'fill-extrusion',
          filter,
          paint: {
            'fill-extrusion-color': color,
            'fill-extrusion-opacity': 0.95,
            'fill-extrusion-height': ['*', ['get', 'median_height'], 0.3048],
          },
          'source-layer': 'buildings_2020',
        },
        {
          type: 'symbol',
          filter,
          minzoom: 17,
          layout: {
            'text-field': ['get', 'loadd1'],
            'text-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              8,
              0,
              16,
              10,
              22,
              16]
          },
          paint: {
            'text-opacity': 1,
            'text-color': 'black',
            'text-halo-width': 1,
            'text-halo-color': 'white',
          },
          'source-layer': 'buildings_2020',
        },
      ]
    },
    buildings_type: function () {
      let filter = ['in', ['get', 'city_id'], ["literal", this.mcdFilter]]
      if (!this.mcdFilter || this.mcdFilter.length === 0) {
        filter = true
      } else if (this.mcdFilter && !this.mcdFilter.length){
        filter = ['==', ['get', "city_id"], this.mcdFilter]
      }
      let color = [
        'match',
        ['get', 'general_type']]
      this.amenityNames[this.mappedAmenity].colors.forEach(c => {
        color.push(c.key, c.value)
      })
      color.push('#ccc')
      return [
        {
          type: 'fill-extrusion',
          filter,
          paint: {
            'fill-extrusion-color': color,
            'fill-extrusion-opacity': 0.95,
            'fill-extrusion-height': ['*', ['get', 'median_height'], 0.3048],
          },
          'source-layer': 'buildings_2020',
        },
        {
          type: 'symbol',
          filter,
          minzoom: 17,
          layout: {
            'text-field': ['get', 'loadd1'],
            'text-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              8,
              0,
              16,
              10,
              22,
              16]
          },
          paint: {
            'text-opacity': 1,
            'text-color': 'black',
            'text-halo-width': 1,
            'text-halo-color': 'white',
          },
          'source-layer': 'buildings_2020',
        },
      ]
    },
    buildings_style: function (){
      if (this.mappedAmenity === 'building_type'){
        return this.buildings_type
      }
      return this.buildings_year
    },
  },
  methods: {
    setMapStyle: function (e) {
      this.map.setStyle(e)
    },
    nextFrameIdle: function () {
      this.animationCaller = setTimeout(this.nextFrame, 1500)
    },
    nextFrame: function () {
      if (this.yearCutOff >= 2020) {
        this.animationCaller = undefined
        return
      } else {
        this.yearCutOff += 10
      }
      this.map.once('idle', this.nextFrameIdle)
    },
    startStopAnimation() {
      if (this.animationCaller) {
        // stop animation
        window.clearTimeout(this.animationCaller)
        this.map.off('idle', this.nextFrameIdle)
        this.animationCaller = undefined
      } else if (this.mappedAmenity !== 'year_built') {
        // nothing to do
      } else {
        if (this.yearCutOff >= 2020) {
          this.yearCutOff = 1900
        }
        this.nextFrame()
      }
    },
    fitToBoundary: function () {
      let bbox = turf.bbox(this.boundary)
      this.map.fitBounds(
          [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
      )
    },
    buildings_update_popup: function (featureEvent) {
      const feature = featureEvent.features[0];
      if (!feature) {
        return;
      }
      const coords = featureEvent.lngLat;
      let html = `
        <h1 class="popup-header">BUILDING: ${feature.properties.building_id}</h1>
        <div class="wrapper">
          <div class="content">
            <table>
                <tbody>
                <tr>
                    <th class="field-header">BUILDING ADDRESS</th>
                    <td class="data">${feature.properties.address}</td>
                </tr>
                <tr>
                    <th class="field-header">COMMUNITY</th>
                    <td class="data">${feature.properties.city_name}</td>
                </tr>
                <tr>
                    <th class="field-header">PARCEL NUMBER</th>
                    <td class="data">${feature.properties.apn}</td>
                </tr>
                <tr>
                    <th class="field-header">BUILDING TYPE</th>
                    <td class="data">${feature.properties.label_name}</td>
                </tr>
                <tr>
                    <th class="field-header">SQUARE FEET</th>
                    <td class="data">${feature.properties.sqft.toLocaleString()}</td>
                </tr>
                <tr>
                    <th class="field-header">NUMBER OF STORIES</th>
                    <td class="data">${feature.properties.stories}</td>
                </tr>
                <tr>
                    <th class="field-header">NUMBER OF HOUSING UNITS</th>
                    <td class="data">${feature.properties.housing_units.toLocaleString()}</td>
                </tr>`
      if (feature.properties.gqcap > 0) {
        html += `<tr>
                    <th class="field-header">GROUP QUARTERS</th>
                    <td class="data">${feature.properties.gqcap}</td>
                </tr>`
      }
      html +=
          `<tr>
                    <th class="field-header">YEAR BUILT</th>
                    <td class="data">${feature.properties.year_built}</td>
                </tr>
                </tbody>
            </table>
          </div>
          <a href="mailto:InfoCenter@semcog.org?subject=Comment on Building ${feature.properties.building_id}">Comment on this Building</a>
        </div>
`;
      this.currentPopup.setLngLat([coords.lng, coords.lat])
          .setHTML(html)
          .addTo(this.map);
    },
  },
  mounted: function () {
    fetch('geojson/cities.geojson')
        .then(res => res.json())
        .then(res => {
          let regionBoundarys = {};
          res.features.forEach(f => {
            regionBoundarys[f.properties.city_id] = f;
          });
          this.regionBoundarys = regionBoundarys;
        });
    this.map.once('idle', () => {
      this.map.on('sourcedata', (e) => {
        if (e.sourceId === 'buildings') {
          if (this.map.getLayer('buildings')) {
            this.$nextTick(() => {
              this.loaded = true
            })
          }
        }
      });
    });
  },
  watch: {
    mcdFilter: function () {
      this.fitToBoundary();
    },
  }
}
</script>

<style scoped>
#map {
  grid-row: 2;
  position: relative;
}

/deep/ .mapboxgl-map {
  width: 100%;
  height: 100%;
}

#legend {
  height: fit-content;
  margin-bottom: 24px;
  margin-right: 10px;
  padding: 8px;
  text-align: center;
  text-align: -webkit-center;
  text-align: -moz-center;
  color-adjust: exact;
}

#legend table {
  border-collapse: collapse;
  align-self: center;
}

/deep/ #legend th select {
  border-radius: unset;
}

/deep/ #legend th select {
  border-color: rgba(243, 243, 243, 0);
}

/deep/ #legend th select:focus {
  outline: 0;
  border: unset;
}

/deep/ #legend th select {
  font-size: 1rem;
  font-weight: bold;
}

#legend td, #legend th {
  border: 1px solid #999;
  padding: 0.2rem;
}

.text_halo {
    text-shadow: -0.05em 0 black, 0 0.05em black,
      0.05em 0 black, 0 -0.05em black;
  color: #ffffff;
}

#legend td {
  text-align: center;
  font-size: small;
  opacity: .3;
}

#legend tr td.activate {
  opacity: .95;
}

.demographic-legend span {
  padding: 2px;
}

div.selectors {
  top: 0;
  bottom: unset;
  height: fit-content;
  margin-top: 20px;
  right: auto;
  margin-left: 20px;
  padding: 8px;
}

.map-overlay {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 1);
  font-family: Arial, sans-serif;
  border-radius: 3px;
}

/deep/ .popup-header{
  background-color: #011f4a;
  color: white;
  padding: 10px;
}

/deep/ .content th {
  border-bottom: 1px solid #ddd;
}

/deep/ .content td {
  border-bottom: 1px solid #ddd;
}

/deep/ .field-header {
  font-weight: bolder;
}

#basemap {
  pointer-events: none;
  position: absolute;
}

.loader {
  position: absolute;
  background: rgba(46, 60, 67, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  opacity: 1;
  transition: opacity 300ms;
  pointer-events: none;
}

.CDB-LoaderIcon--big {
  width: 40px;
  height: 40px;
}

.CDB-LoaderIcon-spinner {
  width: 40px;
  height: 40px;
  animation: rotate 2s linear infinite;
}

.CDB-LoaderIcon-path {
  stroke: rgba(255, 255, 255, .88);
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
  stroke-width: 4px
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0
  }

  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35
  }

  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg)
  }
}

@media print {
  /deep/ .mapboxgl-ctrl-geocoder, .mapboxgl-ctrl-group {
    display: none;
  }

  /deep/ select {
    appearance: none;
    border: none;
    /* needed for Firefox: */
    overflow: hidden;
  }

  /deep/ .mapboxgl-control-container {
    display: none;
  }
}

@media print {
  .no-print {
    display: none !important;
  }
}

@media screen and (max-width: 1000px) {
  #map {
    display: grid;
    grid-template-columns: auto max-content;
    grid-template-rows: auto max-content;
  }

  .map-overlay {
    margin-right: unset;
    position: unset;
  }

  div.selectors {
    margin-left: unset;
    margin-top: unset;
    grid-row: 1;
    grid-column: 2;
  }

  #basemap {
    position: relative;
    grid-area: 1 / 1;
  }

  .mapboxgl-map {
    grid-row-start: 1;
    grid-row-end: 3;
    grid-column: 1;
  }

  #legend {
    grid-row: 2;
    grid-column: 2;
    margin: 0;
  }
}

@media screen and (max-width: 576px) {
  #map {
    display: grid;
    grid-template-rows: auto max-content max-content;
    grid-template-columns: auto;
  }

  div.selectors {
    grid-row: 1;
    grid-column: 1;
  }

  .mapboxgl-map {
    grid-row: 2;
    height: 75vh;
  }

  #basemap {
    position: relative;
    grid-area: 2 / 1;
  }

  #legend {
    grid-row: 3;
    grid-column: 1;
  }
}
</style>
